import React, { ReactNode, useState } from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import { handleAPI } from '../../../utils/api/api';
import Label from '../Label/Label';
import SelectTickIcon from '../../icons/SelectTickIcon';
import DownCaretThinIcon from '../../icons/DownCaretThinIcon';
import XIcon from '../../icons/XIcon';
import { deserializeData } from 'utils/helper/helper';
import AuthWarningTooltip from '../AuthWarningTooltip/AuthWarningTooltip';
import useHasAbility from '../../../hooks/auth/useHasAbility';
import { TooltipData } from '../tooltip/tooltip';
import type { MenuPlacement } from 'react-select';

const findClosestModal = (ref) => {
  return ref?.closest('.modal') || null;
};

const determinePortalTarget = (ref, showOnBody, showOnModal) => {
  if (showOnBody) {
    return document.getElementById('body');
  } else if (showOnModal) {
    return findClosestModal(ref);
  }
  return null;
};

export interface MultiSelectProps {
  endPoint?: string;
  selectedOptions?: any;
  setSelectedOptions?: (args: any) => void;
  isMulti?: boolean;
  options?: Record<string, { id: any; name: string }> | any[];
  placeholder?: string;
  infoData?: TooltipData;
  label?: ReactNode;
  include?: string;
  displayValue?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  isSearchable?: boolean;
  sortOptions?: boolean;
  isClearable?: boolean;
  labelClassName?: string;
  className?: string;
  queryParams?: any;
  menuPlacement?: MenuPlacement;
  showOnBody?: boolean;
  showOnModal?: boolean;
  searchBy?: string;
  filterRecordById?: any;
  error?: string;
  controllerField?: any;
  mapAsyncDataFunc?: (data: any) => any;
  defaultElement?: string;
  onBlur?: (args: any) => void;
  onChange?: (args: any) => void;
  defaultElementOnlyWhenData?: boolean;
  abilitiesKey?: string;
  isReprocessing?: boolean;
  enabledOnly?: boolean;
  forceWideMenu?: boolean;
  testId?: string;
  prependOptions?: any[];
  controlShouldRenderValue?: boolean;
}

const MultiSelect = ({
  endPoint,
  selectedOptions,
  setSelectedOptions,
  isMulti,
  options,
  placeholder = 'Select from the list',
  infoData,
  label,
  include,
  displayValue = 'name',
  isDisabled = false,
  isRequired = false,
  isSearchable = true,
  sortOptions = true,
  isClearable = true,
  labelClassName,
  className,
  queryParams,
  menuPlacement = 'auto',
  showOnBody = false,
  showOnModal = true,
  searchBy = 'filter[name_cont]',
  filterRecordById,
  error,
  controllerField,
  mapAsyncDataFunc,
  defaultElement,
  onBlur,
  onChange,
  defaultElementOnlyWhenData = false,
  abilitiesKey,
  isReprocessing = false,
  enabledOnly = true,
  forceWideMenu = false,
  testId,
  prependOptions,
  controlShouldRenderValue = true,
}: MultiSelectProps) => {
  const hasAbility = useHasAbility(abilitiesKey);
  const [componentRef, setComponentRef] = useState(undefined);
  const [inputValue, setInputValue] = useState('');
  const [authError, setAuthError] = useState(!hasAbility);

  const handleInputChange = (newValue, actionMeta) => {
    if (actionMeta.action === 'input-change') {
      setInputValue(newValue);
    }
  };

  const loadOptions = async (searchQuery, _, { page }) => {
    const searchParam = searchBy;
    const requestParams = {
      page: page,
      per_page: 20,
      ...(searchQuery && { [searchParam]: searchQuery }),
      ...(enabledOnly ? { 'filter[enabled_eq]': true } : {}),
      'filter[s]': `${displayValue} asc`,
      include: include,
      ...queryParams,
    };
    const response = await handleAPI(endPoint, 'get', requestParams);
    if (response?.status === 403) {
      setAuthError(true);
      return { options: [] };
    }

    if (response.status !== 200 && response.status !== 201) {
      return { options: [] };
    }
    let data = [];

    const isPoolGatewayEndpoint = endPoint && endPoint.includes('/pools/') && endPoint.includes('/gateways');

    if (isPoolGatewayEndpoint) {
      data = response.data.included
        .filter((item) => item.type === 'gateway')
        .map((gateway) => ({
          id: gateway.id,
          title: gateway.attributes.title || gateway.attributes.name || `Gateway #${gateway.id}`,
          name: gateway.attributes.name || gateway.attributes.title || `Gateway #${gateway.id}`,
          mid: gateway.attributes.mid || null,
          [displayValue]:
            gateway.attributes[displayValue] ||
            gateway.attributes.title ||
            gateway.attributes.name ||
            `Gateway #${gateway.id}`,
        }));
    } else {
      data = deserializeData(response.data) as any[];
    }

    if (mapAsyncDataFunc) {
      data = mapAsyncDataFunc(data);
    }

    if (filterRecordById) {
      data = data.filter((item) => item?.id !== filterRecordById);
    }

    if (isReprocessing && searchQuery) {
      data = data.filter((item) => {
        const searchFields = [item.title, item.name, item.mid];
        return searchFields.some((field) => field && field.toLowerCase().includes(searchQuery.toLowerCase()));
      });
    }
    let returnData;
    if (isReprocessing) {
      returnData = data.map((item) => ({
        ...item,
        title: item.title || item.name || item.mid || `Gateway #${item.id}`,
        name: item.name || item.title || item.mid || `Gateway #${item.id}`,
        [displayValue]: item[displayValue] || item.title || item.name || item.mid || `Gateway #${item.id}`,
      }));
    } else {
      returnData = include ? data[include] : data ?? response.data;
    }

    const shouldIncludeDefault =
      defaultElement && (!defaultElementOnlyWhenData || (returnData && returnData.length > 0));

    const finalOptions = shouldIncludeDefault ?
      [...[{ [displayValue]: defaultElement, id: 0 }], ...returnData] :
      returnData;

    const finalOptionsWithPrepend = prependOptions?.length ? [...prependOptions, ...finalOptions] : finalOptions;

    return {
      options: finalOptionsWithPrepend,
      hasMore: response.data.meta ? response.data.meta.total_count / 20 > page : false,
      additional: { page: page + 1 },
    };
  };

  const customStaticOptions = async (search) => {
    let optionsToDisplay;
    try {
      optionsToDisplay = Object.values(options).filter((option) => {
        const valueToFilter = option?.[displayValue]?.toLowerCase();
        return valueToFilter.includes(search.toLowerCase());
      });
    } catch (e) {
      console.error(e);
    }

    if (sortOptions) {
      optionsToDisplay.sort((a, b) => {
        const nameA = a?.[displayValue]?.toLowerCase();
        const nameB = b?.[displayValue]?.toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }

    return {
      options: optionsToDisplay,
      hasMore: false,
    };
  };

  const handleOnChange = (data) => {
    controllerField ? controllerField.onChange(data) : setSelectedOptions(data);
    if (onChange) onChange(data);
  };

  const handleOnBlur = (e) => {
    controllerField?.onBlur(e);
    if (onBlur) onBlur(e);
  };

  const menuPortalTarget = determinePortalTarget(componentRef, showOnBody, showOnModal);

  return (
    <div
      data-testid={testId || controllerField?.name || 'multiselect'}
      ref={setComponentRef}
      className={`relative ${authError && label && 'bg-gray-2'} ${isDisabled || authError ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      {authError && label && (
        <div className={`absolute right-0 top-[2px] z-50`}>
          <AuthWarningTooltip />
        </div>
      )}
      {label && (
        <Label
          label={label}
          infoData={infoData}
          required={isRequired}
          className={labelClassName}
        />
      )}
      <AsyncPaginate
        loadOptions={endPoint ? loadOptions : customStaticOptions}
        isMulti={isMulti}
        getOptionValue={(option) => option.id}
        getOptionLabel={(option) => option[displayValue]}
        value={controllerField ? controllerField.value : selectedOptions}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        selectRef={controllerField?.ref}
        additional={{ page: 1 }}
        isSearchable={isSearchable}
        closeMenuOnSelect={!isMulti}
        blurInputOnSelect={false}
        placeholder={placeholder}
        debounceTimeout={endPoint ? 500 : 0}
        controlShouldRenderValue={controlShouldRenderValue}
        components={{
          Option: CustomOption,
          MultiValue: CustomMultiValue,
          IndicatorsContainer: authError && !label ? AuthWarningTooltip : CustomIndicatorsContainer,
        }}
        className={`!pointer-events-auto text-sm ${className}`}
        classNames={{
          control: (state) => `
            px-3 py-[0.5625rem] !min-h-auto !rounded-[5px] relative !bg-transparent
            ${state.isFocused && '!border-primary'}
            ${error ? '!border-error' : '!border-gray-5'}
            ${(authError && !label) || isDisabled ? '!text-gray-5 !cursor-not-allowed' : ''}`,
          valueContainer: () => '!p-0',
          input: () => '!p-0 !m-0 !ml-1 [&>*]:!text-text',
          placeholder: () => '!text-gray-5 !m-0 !text-sm pr-2',
          singleValue: () => '!text-text !p-0 !m-0 !text-sm',
          menu: () =>
            `!shadow-none !bg-transparent !z-[10000] !my-0 ${showOnModal ? '!max-h-[150px]' : ''} ${forceWideMenu ? '!w-auto !min-w-[200px]' : ''}`,
          menuList: () =>
            `!p-0 !rounded-[0.375rem] !z-50 !shadow-select-menu !bg-transparent ${showOnModal ? '!max-h-[150px]' : ''} ${forceWideMenu ? '!w-auto !min-w-[200px]' : ''}`,
          noOptionsMessage: () => 'bg-gray-4',
          loadingMessage: () => 'bg-gray-4',
          menuPortal: () => '!z-[1000]',
        }}
        defaultMenuIsOpen={false}
        isDisabled={isDisabled || authError}
        hideSelectedOptions={false}
        cacheUniqs={[endPoint, options]}
        menuPlacement={menuPlacement}
        menuPortalTarget={menuPortalTarget}
        isClearable={!isRequired && isClearable}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onMenuClose={() => setInputValue('')}
      />
      <p className='text-xs text-error'>{error}</p>
    </div>
  );
};

const CustomOption = ({ innerProps, children, isSelected, isMulti, isDisabled }) => {
  return (
    <div
      data-testid='multiselect-option'
      {...innerProps}
      className={`grid grid-flow-col items-center gap-2 bg-gray-4 px-4 py-[3px] text-sm
        ${isDisabled ? '!cursor-not-allowed text-gray-5' : 'cursor-pointer text-text'}
        ${isSelected && 'font-medium'}
        ${isMulti ? 'gap-2' : 'justify-between'}`}
    >
      {isMulti && (
        <div className={`flex h-6 w-6 items-center justify-center rounded-full ${isSelected ? 'bg-primary' : 'border border-gray-1'} `}>
          {isSelected && (
            <SelectTickIcon
              className='stroke-white stroke-[2px]'
              size='sm'
            />
          )}
        </div>
      )}
      <div className='whitespace-nowrap'>{children}</div>
      {isSelected && !isMulti && <SelectTickIcon className='stroke-primary' />}
    </div>
  );
};

const CustomMultiValue = ({ children, selectProps }) => {
  const values = selectProps.value;
  const displayKey = selectProps.getOptionLabel({
    [selectProps.getOptionValue]: values[values.length - 1]?.[selectProps.getOptionValue],
  });
  const isLastValue = displayKey === children;
  return (
    <div>
      {children}
      {!isLastValue && ',\xa0'}
    </div>
  );
};

const CustomIndicatorsContainer = ({ selectProps, clearValue }) => {
  const shouldShowClearIcon = !selectProps.isRequired && selectProps.isClearable && selectProps.value;

  return (
    <div className='flex items-center'>
      {shouldShowClearIcon && (
        <div
          onTouchStart={selectProps.isDisabled ? undefined : clearValue}
          onClick={selectProps.isDisabled ? undefined : clearValue}
          className={`group px-2 ${selectProps.isDisabled ? 'cursor-not-allowed' : ''}`}
        >
          <XIcon
            className={`stroke-gray-1 ${!selectProps.isDisabled && 'group-hover:brightness-75'}`}
            size='sm'
          />
        </div>
      )}
      <DownCaretThinIcon
        size='lg'
        svgClassName={`transition-all ${selectProps.menuIsOpen && 'rotate-180'}`}
        pathClassName={`!stroke-gray-1  ${selectProps.menuIsOpen && '!stroke-primary'}`}
      />
    </div>
  );
};

export default MultiSelect;
